import type { V1Thread } from '@/generated/hooks/rain/models';
import { formatActivityTimestamp } from '@/shared/utils';
import type { IThreadOption } from '@/shared/components';

/**
 * Builds the base label for a Rain thread, before any duplicate-suffix
 * disambiguation is applied. Untitled threads fall back to the default
 * ("New chat"); their timestamp is shown separately as the row description.
 */
function getBaseThreadLabel(thread: V1Thread, defaultLabel: string): string {
  return thread.title?.trim() || defaultLabel;
}

/**
 * Builds dropdown options with duplicate base labels disambiguated by a
 * "(2)", "(3)" suffix in creation order.
 */
export function buildThreadOptions(
  threads: V1Thread[],
  defaultLabel: string,
): IThreadOption[] {
  const validEntries = threads
    .map(thread => ({ thread, threadId: thread.thread_id }))
    .filter((entry): entry is { thread: V1Thread; threadId: string } =>
      Boolean(entry.threadId),
    );

  // Number duplicates in creation order so the oldest thread keeps the bare
  // label and newer ones get "(2)", "(3)" — independent of the display order.
  const suffixByThreadId = new Map<string, number>();
  const seenByLabel = new Map<string, number>();
  [...validEntries]
    .sort((a, b) =>
      (a.thread.created_at ?? '').localeCompare(b.thread.created_at ?? ''),
    )
    .forEach(entry => {
      const baseLabel = getBaseThreadLabel(entry.thread, defaultLabel);
      const seen = (seenByLabel.get(baseLabel) ?? 0) + 1;
      seenByLabel.set(baseLabel, seen);
      suffixByThreadId.set(entry.threadId, seen);
    });

  return validEntries.map(entry => {
    const baseLabel = getBaseThreadLabel(entry.thread, defaultLabel);
    const seen = suffixByThreadId.get(entry.threadId) ?? 1;
    const label = seen === 1 ? baseLabel : `${baseLabel} (${seen})`;
    return {
      value: entry.threadId,
      label,
      description: formatActivityTimestamp(
        entry.thread.updated_at,
        entry.thread.created_at,
      ),
    };
  });
}
